import React from 'react';

import CloseIcon from '@mui/icons-material/Close';

import { MenuItem } from '@mui/material';

import { StyledAutocomplete, StyledSelect, StyledTextField } from 'components/Input/styles';

import {
  ConditionRemove,
  ConditionRow as ConditionRowGrid,
  ValueCell,
} from 'containers/MemberGroups/components/RuleBuilder/styles';

import { FieldCondition, FieldConditionOperator } from 'utils/api/modules/entities/entities.types';

import ConditionValueControl from './ConditionValueControl';
import { EventFieldCatalogue, IN_PIPELINE_GROUP, PickerField } from './eventFields';
import { CONDITION_OPERATOR_LABELS, isMultiValueOperator, isValueOperator, operatorsForKind } from './model';
import { ConditionGroupLabel } from './styles';

// The Groups condition row with one control changed: Groups picks from thirteen fixed user
// attributes, whereas an account carries roughly three hundred event fields, so the field cell is a
// searchable autocomplete with two group headings.

export interface ConditionRowProps {
  condition: FieldCondition;
  index: number;
  pickerFields: PickerField[];
  catalogue: EventFieldCatalogue;
  /** Display names for stored uuid values, so re-opening a rule shows names rather than uuids. */
  labels: Record<string, string>;
  /** Marks the controls on this row that are still empty, when collapsing was refused. */
  invalid?: boolean;
  onChange: (condition: FieldCondition, labels?: Record<string, string>) => void;
  onRemove: () => void;
}

const ConditionRow: React.FunctionComponent<ConditionRowProps> = ({
  condition,
  index,
  pickerFields,
  catalogue,
  labels,
  invalid = false,
  onChange,
  onRemove,
}) => {
  const selected = pickerFields.find((field) => field.key === condition.attribute) ?? null;

  const operators = operatorsForKind(selected?.kind);

  const onFieldChange = (field: PickerField | null): void => {
    onChange({
      attribute: field?.key ?? '',
      operator: operatorsForKind(field?.kind)[0],
      values: [],
    });
  };

  const onOperatorChange = (operator: FieldConditionOperator): void => {
    const sameShape =
      isValueOperator(operator) &&
      isValueOperator(condition.operator) &&
      isMultiValueOperator(operator) === isMultiValueOperator(condition.operator);
    onChange({ ...condition, operator, values: sameShape ? condition.values : [] });
  };

  const renderGroup = (params: { key: number; group: string; children?: React.ReactNode }): React.ReactNode => (
    <li key={params.key}>
      <ConditionGroupLabel
        data-testid={
          params.group === IN_PIPELINE_GROUP
            ? 'condition-attribute-group-in-pipeline'
            : 'condition-attribute-group-other'
        }
      >
        {params.group}
      </ConditionGroupLabel>
      <ul className="MuiAutocomplete-groupUl">{params.children}</ul>
    </li>
  );

  return (
    <ConditionRowGrid data-testid={`condition-row-${index}`}>
      <StyledAutocomplete
        fullWidth
        size="small"
        data-testid={`condition-attribute-${index}`}
        options={pickerFields}
        value={selected}
        groupBy={(option: PickerField) => option.group}
        getOptionLabel={(option: PickerField) => option.name}
        isOptionEqualToValue={(a: PickerField, b: PickerField) => a.key === b.key}
        renderGroup={renderGroup}
        onChange={(_, option) => onFieldChange(option as PickerField | null)}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            size="small"
            error={invalid && !condition.attribute}
            placeholder={condition.attribute && !selected ? condition.attribute : 'Select a field'}
            inputProps={{ ...params.inputProps, 'aria-label': 'Field' }}
          />
        )}
      />

      <StyledSelect
        fullWidth
        size="small"
        data-testid={`condition-operator-${index}`}
        disabled={!condition.attribute}
        value={condition.operator}
        inputProps={{ 'aria-label': 'Operator' }}
        onChange={(event) => onOperatorChange(event.target.value as FieldConditionOperator)}
      >
        {operators.map((operator) => (
          <MenuItem key={operator} value={operator}>
            {CONDITION_OPERATOR_LABELS[operator]}
          </MenuItem>
        ))}
      </StyledSelect>

      {condition.attribute ? (
        <ConditionValueControl
          source={catalogue.valueSources[condition.attribute]}
          operator={condition.operator}
          values={condition.values}
          labels={labels}
          error={invalid}
          testId={`condition-values-${index}`}
          onChange={(values, valueLabels) => onChange({ ...condition, values }, valueLabels)}
        />
      ) : (
        <ValueCell data-testid={`condition-values-${index}`}>
          <StyledTextField fullWidth size="small" disabled placeholder="Values" aria-hidden />
        </ValueCell>
      )}

      <ConditionRemove
        type="button"
        data-testid={`condition-remove-${index}`}
        aria-label="Remove condition"
        onClick={onRemove}
      >
        <CloseIcon fontSize="small" />
      </ConditionRemove>
    </ConditionRowGrid>
  );
};

export default ConditionRow;
